import React, { useId } from 'react';

const DIYA_ASSET = '/assets/october-templates/vedic-diya-icon.png';
const BELL_ASSET = '/assets/october-templates/vedic-temple-bell-icon.png';

/** Ornate brass diya — generated vector asset (transparent PNG). */
export function DiyaIcon({ className = '', size = 48 }: { className?: string; size?: number }) {
  return (
    <img
      className={`vmd-diya-asset ${className}`.trim()}
      src={DIYA_ASSET}
      alt=""
      width={size}
      height={size}
      draggable={false}
      aria-hidden
    />
  );
}

/** Traditional temple ghanta — generated vector asset (transparent PNG). */
export function TempleBellIcon({ className = '', size = 52 }: { className?: string; size?: number }) {
  return (
    <img
      className={`vmd-bell-asset ${className}`.trim()}
      src={BELL_ASSET}
      alt=""
      width={size}
      height={size}
      draggable={false}
      aria-hidden
    />
  );
}

/** Inline SVG diya fallback for places that need CSS flame animation hooks. */
export function DiyaIconSvg({ className = '', size = 48 }: { className?: string; size?: number }) {
  const uid = useId().replace(/:/g, '');
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden
    >
      <ellipse cx="32" cy="52" rx="20" ry="5" fill="#5C0F1C" opacity="0.28" />
      <path
        d="M12 44c1.5-9 10-15 20-15s18.5 6 20 15c-3.5 5.5-11 9-20 9s-16.5-3.5-20-9Z"
        fill={`url(#${uid}-bowl)`}
        stroke="#E8C96A"
        strokeWidth="1.1"
      />
      <path
        d="M16 42c2-3 8-5 16-5s14 2 16 5"
        stroke="#8B5A12"
        strokeWidth="1"
        opacity="0.55"
      />
      <ellipse cx="32" cy="40" rx="12" ry="3.2" fill="#2a0a10" />
      <path
        d="M32 39c.4-7 3.2-13 3.5-18.5C35.5 20 29 24 28.5 32.5c-.2 3.2 1.4 5.5 3.5 6.5Z"
        fill={`url(#${uid}-flame)`}
        className="diya-flame"
      />
      <ellipse cx="32" cy="27" rx="1.8" ry="2.4" fill="#FFF8D8" opacity="0.95" />
      <defs>
        <linearGradient id={`${uid}-bowl`} x1="12" y1="28" x2="52" y2="50">
          <stop stopColor="#F0D78C" />
          <stop offset="0.45" stopColor="#C9A227" />
          <stop offset="1" stopColor="#6B3F14" />
        </linearGradient>
        <linearGradient id={`${uid}-flame`} x1="32" y1="16" x2="32" y2="40">
          <stop stopColor="#FFF6C8" />
          <stop offset="0.4" stopColor="#F0A820" />
          <stop offset="1" stopColor="#C0392B" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Temple gopuram silhouette for sacred hero backgrounds. */
export function TempleSilhouette({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 400 160" fill="none" aria-hidden preserveAspectRatio="xMidYMax meet">
      <path
        d="M200 8 L230 48 H210 V70 H250 L235 95 H265 V120 H290 L275 145 H125 L110 120 H135 V95 H165 L150 70 H190 V48 H170 Z"
        fill="#1a0608"
        stroke="#E0C068"
        strokeWidth="1.5"
        opacity="0.85"
      />
      <rect x="185" y="118" width="30" height="28" rx="2" fill="#2a0a10" stroke="#D4AF37" strokeWidth="1" />
      <circle cx="200" cy="55" r="6" fill="#F0A820" opacity="0.7" />
      <path d="M40 145 H360" stroke="#E0C068" strokeWidth="1" opacity="0.4" />
    </svg>
  );
}

/** Marigold / mango-leaf toran spanning the top of a section. */
export function ToranBorder({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 360 48" fill="none" aria-hidden preserveAspectRatio="none">
      <path d="M0 8 H360" stroke="#E0C068" strokeWidth="1.5" opacity="0.5" />
      {Array.from({ length: 13 }).map((_, i) => {
        const x = 20 + i * 27;
        return (
          <g key={i} transform={`translate(${x},12)`}>
            <ellipse cx="0" cy="10" rx="7" ry="10" fill={i % 2 === 0 ? '#F0A820' : '#C0392B'} opacity="0.9" />
            <path d="M0 0 Q4 6 0 20 Q-4 6 0 0" fill="#2d5a27" opacity="0.85" />
          </g>
        );
      })}
    </svg>
  );
}

/** Initials helper for wax-seal monograms. */
export function coupleMonogram(title: string): string {
  const parts = title
    .split(/\s*&\s*|\s+and\s+/i)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0] || '';
    const b = parts[1][0] || '';
    return `${a}&${b}`.toUpperCase();
  }
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  return (title.trim().slice(0, 2) || '✦').toUpperCase();
}
